import { forwardRef } from 'react';

import { Badge, BadgeProps } from './badge';

export type NumberedBadgeProps = {
  /**
   * The number to display in the badge
   */
  number: number;

  /**
   * Optional label for accessibility
   */
  label?: string;
} & Omit<BadgeProps, 'children'>;

/**
 * A badge component that displays a number, commonly used for indexing or counting items.
 *
 * @example
 * ```tsx
 * <NumberedBadge number={1} />
 * <NumberedBadge number={42} size="md" />
 * ```
 */
export const NumberedBadge = forwardRef<HTMLSpanElement, NumberedBadgeProps>(
  ({ number, label, size = 'sm', ...props }, ref) => {
    return (
      <Badge
        ref={ref}
        background="gray.100"
        size={size}
        borderRadius="full"
        border="none"
        color="gray.700"
        fontWeight="bold"
        aria-label={label}
        {...props}
      >
        {number}
      </Badge>
    );
  },
);

NumberedBadge.displayName = 'NumberedBadge';
